<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading</div>
    <div v-if="loaded" class="root">

      <div class="column">

        <!-- Minimal header -->
        <header class="header">
          <img :src="lightLogoUrl" class="logo" />
    
        </header>

        <div class="rule"></div>

        <!-- Partnership line -->
        <div class="partnership">
          <span class="p-lo">{{ loName || "Loan Officer" }}</span>
          <span class="p-and">and</span>
          <span class="p-re">{{ realtorName || realtor?.partner_title || 'Partner' }}</span>
        </div>

        <div class="rule"></div>

        <!-- Hero -->
        <section class="hero">
       
          <h1 class="hero-h1" v-html="heroH1Html"></h1>
          <p class="hero-body">{{ heroBody }}</p>
          <div class="hero-actions">
            <a class="act-primary" :href="applyRightLink">{{ heroPrimaryText }}</a>
            <a v-if="applyLeftLink" class="act-ghost" :href="applyLeftLink">{{ heroSecondaryText }}</a>
          </div>
        </section>

        <div class="rule"></div>

        <!-- LO profile — typographic -->
        <section class="profile-section">
          <div class="ps-meta">
            <span class="ps-tag">Loan Officer</span>
          </div>
          <div class="ps-main">
            <div class="ps-left">
              <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="ps-photo" />
            </div>
            <div class="ps-right">
              <div class="ps-name">{{ loName }}</div>
              <div class="ps-role">{{ loTitle }}</div>
              <div v-if="loinfo && loinfo.nmls" class="ps-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
              <div class="ps-contacts">
                <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="ps-link">{{ loinfo.phone_number }}</a>
                <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="ps-link">{{ loinfo.email }}</a>
              </div>
          
            </div>
          </div>
        </section>

        <div class="rule rule-accent"></div>

        <!-- Realtor profile — typographic -->
        <section class="profile-section">
          <div class="ps-meta">
            <span class="ps-tag re-ps-tag">{{ realtor?.partner_title || 'Partner' }}</span>
          </div>
          <div class="ps-main">
            <div class="ps-left">
              <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="ps-photo" />
            </div>
            <div class="ps-right">
              <div class="ps-name">{{ realtorName }}</div>
              <div class="ps-role">{{ realtor && realtor.brokerage_name ? realtor.brokerage_name : "" }}</div>
              <div v-if="realtor && realtor.nmls" class="ps-nmls">NMLS {{ (realtor.nmls || "").trim() }}</div>
              <div class="ps-contacts">
                <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="ps-link re-ps-link">{{ realtor.phone_number }}</a>
                <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="ps-link re-ps-link">{{ realtor.email }}</a>
                <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="ps-link re-ps-link" target="_blank" rel="noreferrer">Website</a>
              </div>
            </div>
          </div>
        </section>

        <div class="rule"></div>

        <!-- Footer -->
        <footer class="footer">
          <span class="ft-text">{{ loName }} × {{ realtorName }}</span>
          <a class="ft-link" :href="applyRightLink">{{ heroPrimaryText }}</a>
        </footer>

      </div>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending:        { primary: "#eb610a", accent: "#646363" },
  radiantmortgage:    { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage:     { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam:         { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage:         { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage:     { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial:  { primary: "#0b1726", accent: "#c69c39" },
};
const DBA_BASE = "https://api.goldstarfinancial.com/DBA";
function dbaLogo(brandKey, variant = "primary") { return `${DBA_BASE}/${brandKey}/${variant}.png`; }
function getBrandKey(email) {
  if (!email) return null;
  const d = email.slice(email.indexOf("@") + 1).toLowerCase();
  return d.split(".")[0] || null;
}
export default {
  name: "CoBrandLinen",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    lightLogoUrl() { return dbaLogo(this.brandKey, "primary"); },
    
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Ready to get<br/><em>started?</em>";
      const parts = h.split(",");
      if (parts.length > 1) {
        const a = parts[0].trim();
        const b = parts.slice(1).join(",").trim();
        return `${a}<br/><em>${b}</em>`;
      }
      return h;
    },
    
    heroHeading() { return this.loinfo?.text_section_heading || "About"; },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      const p = [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim();
      return p || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Editoria+Italics:ital@1&family=Instrument+Serif:ital@0;1&family=Jost:wght@300;400;500&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,500;1,300;1,500&family=Jost:wght@300;400;500&display=swap');

.page {
  min-height: 100vh;
  background: #fefefe;
  color: #1a1a1a;
  font-family: 'Jost', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --rule: rgba(26,26,26,0.12);
  --muted: #888;
  --a-rule: color-mix(in srgb, var(--a) 40%, transparent);
}
.root { min-height: 100vh; }

.column {
  max-width: 680px;
  margin: 0 auto;
  padding: 0 32px;
}

/* HEADER */
.header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 36px 0 28px;
}
.logo { width: 160px; max-height:80px; object-fit: contain; }
.header-apply {
  font-size: 12px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase;
  text-decoration: none; color: var(--p);
  border-bottom: 1px solid var(--p); padding-bottom: 2px;
  transition: color 0.18s, border-color 0.18s;
  padding-right:25px;
}
.header-apply:hover { color: var(--a); border-color: var(--a); }

/* RULES */
.rule { height: 1px; background: var(--rule); margin: 0; }
.rule-accent { background: linear-gradient(90deg, var(--a), transparent); opacity: 0.6; }

/* PARTNERSHIP */
.partnership {
  display: flex; align-items: center; gap: 12px;
  padding: 18px 0;
  font-size: 13px; font-weight: 300; letter-spacing: 0.06em;
}
.p-lo { color: var(--p); font-weight: 500; }
.p-and { color: var(--muted); font-style: italic; font-family: 'Cormorant', serif; font-size: 15px; }
.p-re { color: var(--muted); }

/* HERO */
.hero { padding: 90px 0 44px; }
.hero-kicker {
  font-size: 10px; font-weight: 500; letter-spacing: 0.28em; text-transform: uppercase;
  color: var(--a); margin-bottom: 20px;
}
.hero-h1 {
  font-family: 'Cormorant', serif;
  font-size: clamp(46px, 7vw, 80px);
  font-weight: 300; line-height: 1.08;
  color: var(--p); margin: 0 0 22px;
  letter-spacing: -0.01em;
}
.hero-h1 em { font-style: italic; color: var(--a); }
.hero-body { font-size: 15px; line-height: 1.8; color: var(--muted); margin-bottom: 28px; font-weight: 300; }
.hero-actions { display: flex; align-items: center; gap: 24px; justify-content: center;}
.act-primary {
  font-size: 12px; font-weight: 500; letter-spacing: 0.12em; text-transform: uppercase;
  text-decoration: none; color: #fff; background: var(--p);
  padding: 12px 26px; border-radius: 4px; transition: opacity 0.18s;
  width:55%
}
.act-primary:hover { opacity: 0.85; }
.act-ghost {
  font-size: 12px; font-weight: 400; text-decoration: none;
  color: var(--muted); letter-spacing: 0.04em;
  border-bottom: 1px solid var(--rule); padding-bottom: 2px;
  transition: color 0.18s;
}
.act-ghost:hover { color: var(--p); }

/* PROFILE SECTIONS */
.profile-section { padding: 40px 0; }
.ps-meta { margin-bottom: 20px; }
.ps-tag {
  font-size: 10px; font-weight: 500; letter-spacing: 0.24em; text-transform: uppercase;
  color: var(--muted);
}
.re-ps-tag { color: color-mix(in srgb, var(--a) 75%, black); }
.ps-main { display: flex; gap: 24px; align-items: flex-start; }
.ps-photo { width: 72px; height: 72px; object-fit: cover; border-radius: 6px; flex-shrink: 0; }
.ps-name {
  font-family: 'Cormorant', serif;
  font-size: 28px; font-weight: 500; color: var(--p);
  line-height: 1.1; margin-bottom: 4px;
}
.ps-role { font-size: 13px; color: var(--muted); margin-bottom: 2px; font-weight: 300; }
.ps-nmls { font-size: 11px; color: color-mix(in srgb, var(--muted) 65%, transparent); margin-bottom: 12px; }
.ps-contacts { display: flex; flex-direction: column; gap: 3px; margin-bottom: 14px; }
.ps-link {
  font-size: 12px; font-weight: 300; text-decoration: none; color: var(--muted);
  border-bottom: 1px solid var(--rule); width: fit-content; padding-bottom: 1px;
  transition: color 0.18s;
}
.ps-link:hover { color: var(--p); }
.re-ps-link:hover { color: color-mix(in srgb, var(--a) 80%, black); }
.ps-cta {
  font-size: 12px; font-weight: 500; letter-spacing: 0.1em; text-transform: uppercase;
  text-decoration: none; color: var(--p);
  border-bottom: 1.5px solid var(--p); padding-bottom: 2px;
  transition: color 0.18s, border-color 0.18s;
}
.ps-cta:hover { color: var(--a); border-color: var(--a); }

/* FOOTER */
.footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 24px 0 40px;
}
.ft-text { font-size: 12px; color: var(--muted); font-weight: 300; letter-spacing: 0.08em; }
.ft-link {
  font-size: 12px; font-weight: 500; text-decoration: none; color: var(--p);
  letter-spacing: 0.08em; text-transform: uppercase;
  border-bottom: 1px solid var(--p); padding-bottom: 2px;
  transition: color 0.18s;
}
.ft-link:hover { color: var(--a); border-color: var(--a); }
</style>
